import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnDestroy, OnInit } from "@angular/core";
import { CoreLogService } from "../../services/log.service";
import { WebShopService } from "../../services/webShop.service";
import { CoreLanguageService } from "../../services/language.service";
import { IBasketItem, IBasketItemExtra, IMenuCourse, IOrderedItem, OrderedItemState } from "./basketItemCard.facade";
import Utils from "../../shared/utils";
import DateManager from "../../shared/dateManager";
import { DateFormatTyps, VoucherServiceType, WebShopMode } from "../../shared/contentTypes";
import { take } from "rxjs/operators";
import { RemoteGroupOrderService } from "../../services/RemoteGroupOrder.service";
import { Router } from "@angular/router";
import { Observable } from "rxjs";
import { CouponRedemptionService } from "../../services/couponRedemption.service";

@Component({
  selector: "ya-basketItemCard",
  templateUrl: "./basketItemCard.component.html",
  styleUrls: ["./basketItemCard.component.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush
})

export class BasketItemCardComponent implements OnInit, OnDestroy {
  @Input() basketItem: IBasketItem = null;
  @Input() OrderedItem: IOrderedItem = null;
  @Input() menucourses$: Observable<IMenuCourse[]> = null;
  /** Defaults to `true` meaning the basket item can be modified. 
   * For group orders (e.g., group basket pages), set this value dynamically. */
  @Input() canModifyBasket: boolean = true;
  @Input() isInHouseGroupOrder: boolean = false;

  public stateValue = OrderedItemState;
  public editingStates: { [key: number]: boolean } = {};
  public allowBasketModification: boolean = false;
  public isRemoteGroupOrder: boolean = false;
  public isInHouseOrder: boolean = false;

  public get isChargeWebVoucher(): boolean {
    const me = this;
    const chargeWebVoucher = me._webShopService.voucherServiceType === VoucherServiceType.Charge;
    return chargeWebVoucher;
  }

  constructor(
    private _webShopService: WebShopService,
    private _remoteGroupOrderService: RemoteGroupOrderService,
    private _changeDetectorRef: ChangeDetectorRef,
    private _coreLogService: CoreLogService,
    private _coreLanguageService: CoreLanguageService,
    private _router: Router,
    private _couponRedemptionService: CouponRedemptionService
  ) { }

  ngOnInit() {
    const me = this;
    me._verifyOrderMode();
    me.allowBasketModification = me._webShopService.allowBasketModification;
  }

  ngOnDestroy() { }

  private PushChanges(): void {
    try {
      this._changeDetectorRef.detectChanges();
    } catch (e) {
      this._coreLogService.error("BasketItemCardComponent.PushChanges", "_changeDetectorRef.detectChanges (catch)", e);
    }
  }

  public customSpace(basketItem: IBasketItem) {
    const hasExtra = basketItem.extras && basketItem.extras.length > 0;
    return !basketItem.isWebVoucher && (!this.allowBasketModification || !hasExtra);
  }

  public hasContent(comment: string) {
    return !Utils.IsNullOrEmpty(comment);
  }

  public formatItemDate(validDate: number) {
    return DateManager.MomentAsString(Utils.GetMomentFromInt(validDate), DateFormatTyps.DDMMYYYY);
  }

  public getDepositText(deposit: number, count: number): string {
    const me = this;
    const priceText = me._webShopService.formatCurrency(deposit * count);
    return me._coreLanguageService.value("phWebshop_Shippingcart_IncludeDeposit_BASE")
      .replace("{deposit}", priceText);
  }

  public benefitCostPoints(costPoints: number): string {
    const me = this;
    return me._coreLanguageService.value("phBenefit_Redeem_Cost_Points_Info").replace("{points}", costPoints.toString());
  }

  public displayVariablePrice(variablePrice: number): boolean {
    const me = this;
    const _variablePrice = variablePrice && variablePrice > 0;
    return !me.isInHouseOrder && (_variablePrice || !me.allowBasketModification);
  }

  public setItemPrice(price: number) {
    const me = this;
    return me._webShopService.formatCurrency(price);
  }

  public isWebVoucherMailDelivery(deliveryType: number) {
    let mailDelivery = 0;
    return deliveryType === mailDelivery ? true : false;
  }

  /** Determines if the current order mode is a remote group order. */
  private _isRemoteOrderGroup(orderMode: WebShopMode): boolean {
    const me = this;
    const group_delivery = orderMode === WebShopMode.DeliveryGroup;
    const group_pickup = orderMode === WebShopMode.PickupGroup;
    return me.isRemoteGroupOrder = group_delivery || group_pickup;
  }

  /** Determines if the current order mode is a in-house order. */
  private _isInHouseOrder(orderMode: WebShopMode): boolean {
    const me = this;
    const isInHouse = orderMode === WebShopMode.OrderToTable;
    return me.isInHouseOrder = isInHouse;
  }

  private _verifyOrderMode(): void {
    const me = this;
    const orderMode = me._webShopService.getServiceMode();
    me._isRemoteOrderGroup(orderMode);
    me._isInHouseOrder(orderMode);
  }

  public setExtraString(extraItems: IBasketItemExtra[]): string {
    if (extraItems && extraItems.length > 0) {
      return extraItems.filter(e => e.subIndex >= 0).map(e => {
        const single = (e.count > 1 ? e.count + "x " : "") + e.title;
        if (e.subIndex > 0) {
          const subExtras = extraItems.filter(s => s.subIndex === -e.subIndex);
          if (subExtras.length > 0) {
            return single + "(" + subExtras.map(s => (s.count > 1 ? s.count + "x " : "") + s.title).join(", ") + ")";
          }
        } else {
          return single;
        }
      }).join(", ");
    } else {
      return "";
    }
  }

  public isEditing(itemId: number): boolean {
    return this.editingStates[itemId] || false;
  }

  // Show textarea for editing
  public showTextarea(itemId: number): void {
    this.editingStates[itemId] = true;
    setTimeout(() => {
      // Ensure the item isn't already focused
      if (!this.isEditing(itemId)) {
        // Close the textarea
        this.editingStates[itemId] = false;
      }
    }, 600);
  }

  /** Handle blur event */
  public onBlur(basketItem: IBasketItem, comment: string): void {
    const me = this;
    if (comment && !comment.trim()) {
      // Hide textarea
      me.editingStates[basketItem.id] = false;
    } else {
      // Hide textarea but keep the comment
      me.editingStates[basketItem.id] = false;
    }
    // Update the comment 
    if (basketItem.comment !== comment) {
      basketItem.setcomment(comment);
      me.PushChanges();
    }
  }

  public canDisplayNote(): boolean {
    const me = this;
    const orderMode = me._webShopService.getServiceMode();
    let _caDisplayNote: boolean;
    if (orderMode === WebShopMode.Ticket) {
      _caDisplayNote = false;
    } else if (orderMode === WebShopMode.Webvoucher) {
      _caDisplayNote = false;
    } else {
      _caDisplayNote = true;
    }
    return _caDisplayNote;
  }

  // Handle button toggle menucourse change
  public toggleGroupChange(event, basketItem: IBasketItem) {
    const toggle = event.source;
    if (toggle) {
      if (event.value.some(item => item === toggle.value)) {
        toggle.buttonToggleGroup.value = [toggle.value];
        basketItem.menucourse = toggle.value;
      } else {
        basketItem.menucourse = 0;
        this._coreLogService.error("BasketComponent.toggleGroupChange", "event.value has no toggle.value", event.value, toggle.value);
      }
    } else {
      // If no toggle
      basketItem.menucourse = 0;
    }
  }

  public canChangeCount(canChangeCount: boolean) {
    const me = this;
    return me.canModifyBasket && canChangeCount;
  }

  public handleIncrementItem(basekItem: IBasketItem): void {
    const me = this;
    if (me.isRemoteGroupOrder) {
      let finishing = false;
      me._remoteGroupOrderService.groupStatusPaymentStarted$.pipe(take(1)).subscribe(value => finishing = value).unsubscribe();
      finishing ? me._remoteGroupOrderService.notifyPaymentStarted(false) : basekItem.increment();
    } else {
      basekItem.increment();
    }
  }

  public handleDecrementItem(basekItm: IBasketItem) {
    const me = this;
    if (me.isRemoteGroupOrder) {
      let finishing = false;
      me._remoteGroupOrderService.groupStatusPaymentStarted$.pipe(take(1)).subscribe(value => finishing = value).unsubscribe();
      finishing ? me._remoteGroupOrderService.notifyPaymentStarted(false) : basekItm.decrement();
    } else {
      basekItm.decrement();
    }
  }

  /** Navigates to the article detail page; marks a coupon basket-line re-open so the target page can lock its add action. */
  public async viewDetails(basketItem: IBasketItem) {
    const currentLocation = this._webShopService.locationID;
    const { isWebVoucher, isBenefit, id, webVoucher_id, product_id, coupon_id } = basketItem;
    if (isWebVoucher && !this.isChargeWebVoucher) {
      return this._router.navigate(["/webVoucherCustomDetail", webVoucher_id, currentLocation]);
    }

    if (isBenefit) {
      return this._router.navigate(["/bonusaction"]);
    }

    // Determine the productId based on the context (group order, IH, or default)
    let productId: number;
    if (this.isInHouseGroupOrder) {
      // Convert POS product id to YourApp product id for IH group order
      const product = await this._webShopService.getProductFromPOSProductID(currentLocation, id);
      productId = product.id;
    } else {
      // Use `id` for remote group orders, and `product_id` for default carts
      productId = this.isRemoteGroupOrder ? id : product_id;
    }

    if (coupon_id) {
      await this._couponRedemptionService.markBasketCouponRowNavigation(productId);
    }

    // Navigate to the menu detail page with the appropriate product ID
    this._router.navigate(["menudetail", -1, productId, currentLocation]);
  }



}

